import { useMemo, type ReactElement } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSession } from "@baseline/web/auth";
import { supabase } from "@baseline/web/supabase";
import { FiX } from "react-icons/fi";
import LandingPage from "../components/landing/LandingPage";
import { LibraryTable } from "../components/LibraryTable";
import { ProjectCard } from "../components/ProjectCard";
import { RecentItemsList } from "../components/RecentItemsList";
import { UrlInputForm } from "../components/UrlInputForm";
import { ITEM_TYPE_DISPLAY } from "../components/libraryShared";
import { useLibrary } from "../lib/useLibrary";
import { useProjects } from "../lib/useProjects";

export const Route = createFileRoute("/")({
	validateSearch: (search: Record<string, unknown>) => ({
		mode:
			search.mode === "overview"
				? ("overview" as const)
				: search.mode === "library"
					? ("library" as const)
					: undefined,
		tag: typeof search.tag === "string" ? search.tag : undefined,
		type: typeof search.type === "string" ? search.type : undefined,
		summary: search.summary === "on" ? ("on" as const) : undefined,
	}),
	component: HomePage,
});

function getSessionId(): string {
	const STORAGE_KEY = "findings_session_id";
	let sessionId = localStorage.getItem(STORAGE_KEY);
	if (!sessionId) {
		sessionId = crypto.randomUUID();
		localStorage.setItem(STORAGE_KEY, sessionId);
	}
	return sessionId;
}

if (import.meta.env.DEV) {
	const devHelpers = {
		/**
		 * Process a URL through the extraction pipeline
		 * @example await findings.processUrl("https://youtube.com/watch?v=xxx")
		 * @example await findings.processUrl("https://youtube.com/watch?v=xxx", { skipLlm: true })
		 */
		processUrl: async (
			url: string,
			options?: { skipLlm?: boolean },
		): Promise<unknown> => {
			const { data, error } = await supabase.functions.invoke(
				"process-url",
				{
					body: {
						url,
						sessionId: getSessionId(),
						skipLlm: options?.skipLlm ?? false,
					},
				},
			);
			if (error) throw error;
			console.log("processUrl result:", JSON.stringify(data, null, 2));
			return data;
		},

		/**
		 * Get content by ID
		 * @example await findings.getContent("uuid-here")
		 */
		getContent: async (contentId: string): Promise<unknown> => {
			const { data, error } = await supabase
				.from("contents")
				.select("*")
				.eq("id", contentId)
				.single();
			if (error) throw error;
			console.log("getContent result:", JSON.stringify(data, null, 2));
			return data;
		},

		/** Current session ID */
		sessionId: getSessionId(),
	};

	(window as unknown as { findings: typeof devHelpers }).findings =
		devHelpers;
	console.log(
		"Dev helpers:",
		JSON.stringify(
			{
				processUrl: "findings.processUrl(url, {skipLlm})",
				getContent: "findings.getContent(id)",
				sessionId: "findings.sessionId",
			},
			null,
			2,
		),
	);
}

function HomePage(): ReactElement {
	const { data: session, isPending: sessionPending } = useSession();
	const { mode, tag, type, summary } = Route.useSearch();
	const userId = session?.user?.id;
	const library = useLibrary(userId);
	const projects = useProjects(userId);

	if (sessionPending) {
		return <div className="pt-24" />;
	}

	if (userId) {
		return (
			<AuthenticatedHome
				library={library}
				projects={projects}
				mode={mode}
				tag={tag}
				type={type}
				summary={summary}
			/>
		);
	}

	return <LandingPage />;
}

function AuthenticatedHome({
	library,
	projects,
	mode,
	tag,
	type,
	summary,
}: {
	library: ReturnType<typeof useLibrary>;
	projects: ReturnType<typeof useProjects>;
	mode: "overview" | "library" | undefined;
	tag: string | undefined;
	type: string | undefined;
	summary: "on" | undefined;
}): ReactElement {
	const isLibraryView =
		mode === "library" ||
		(mode !== "overview" &&
			(tag !== undefined || type !== undefined || summary === "on"));
	const showSummary = summary === "on";
	const nonDocumentItems = useMemo(() => {
		if (!library.data) return undefined;
		return library.data.filter((item) => item.source_type !== "document");
	}, [library.data]);
	const filteredItems = useMemo(() => {
		let items = library.data;
		if (!items) return items;
		if (tag) {
			items = items.filter((item) =>
				item.library_item_tags?.some((row) => row.tags.name === tag),
			);
		}
		if (type) {
			items = items.filter((item) => item.item_type === type);
		}
		return items;
	}, [library.data, tag, type]);

	return (
		<div className="mx-auto max-w-[960px] px-6 pt-10">
			<UrlInputForm className="mb-10" />

			<div className="mb-6 flex items-center justify-between gap-4">
				<div className="border-line flex overflow-hidden rounded-md border">
					<Link
						to="/"
						search={{ mode: "overview", tag, type, summary }}
						className={`px-3 py-1.5 text-xs transition-colors ${
							!isLibraryView
								? "bg-surface-raised text-fg"
								: "text-fg-ghost hover:text-fg-tertiary"
						}`}
					>
						Overview
					</Link>
					<Link
						to="/"
						search={{ mode: "library", tag, type, summary }}
						className={`border-line border-l px-3 py-1.5 text-xs transition-colors ${
							isLibraryView
								? "bg-surface-raised text-fg"
								: "text-fg-ghost hover:text-fg-tertiary"
						}`}
					>
						Library
					</Link>
				</div>

				{isLibraryView && (
					<Link
						to="/"
						search={{
							mode: "library",
							tag,
							type,
							summary: showSummary ? undefined : "on",
						}}
						className={`text-xs transition-colors ${
							showSummary
								? "text-fg"
								: "text-fg-ghost hover:text-fg-tertiary"
						}`}
					>
						Summaries
					</Link>
				)}
			</div>

			{isLibraryView ? (
				<>
					{(tag || type) && (
						<div className="mb-4 flex flex-wrap items-center gap-2">
							{tag && (
								<div className="flex items-center gap-2">
									<span className="text-fg-faint text-sm">
										Tag:
									</span>
									<span className="tag-pill">{tag}</span>
									<Link
										to="/"
										search={{
											mode: "library",
											tag: undefined,
											type,
											summary,
										}}
										className="text-fg-ghost hover:text-fg-tertiary transition-colors"
									>
										<FiX className="h-4 w-4" />
									</Link>
								</div>
							)}
							{type && (
								<div className="flex items-center gap-2">
									<span className="text-fg-faint text-sm">
										Type:
									</span>
									<span className="tag-pill">
										{ITEM_TYPE_DISPLAY[type] ?? type}
									</span>
									<Link
										to="/"
										search={{
											mode: "library",
											tag,
											type: undefined,
											summary,
										}}
										className="text-fg-ghost hover:text-fg-tertiary transition-colors"
									>
										<FiX className="h-4 w-4" />
									</Link>
								</div>
							)}
						</div>
					)}

					<LibraryTable
						items={filteredItems}
						isLoading={library.isLoading}
						showSummary={showSummary}
						tag={tag}
						type={type}
						summary={summary}
					/>
				</>
			) : (
				<>
					{/* Projects */}
					{(projects.data?.length ?? 0) > 0 && (
						<div className="mb-10">
							<h2 className="text-fg-ghost mb-4 text-[11px] tracking-widest uppercase">
								Projects
							</h2>
							<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
								{projects.data!.map((project) => (
									<ProjectCard
										key={project.id}
										project={project}
									/>
								))}
							</div>
						</div>
					)}

					{/* Recent Items */}
					<div>
						<h2 className="text-fg-ghost mb-4 text-[11px] tracking-widest uppercase">
							Recent Items
						</h2>
						<RecentItemsList
							items={nonDocumentItems}
							isLoading={library.isLoading}
						/>
					</div>
				</>
			)}
		</div>
	);
}
